/**
 * Scope switcher for admins testing personal vs organization workspace (QA).
 * Regular users use role-based routing — no workspace switching.
 */

import { useNavigate } from 'react-router-dom'
import { Building2, ChevronDown, User } from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { useAuth } from '@/contexts/AuthContext'
import { useActiveContext } from '@/contexts/ActiveContextProvider'
import type { ActiveContext } from '@/types/active-context'
import { getHomeRouteForContext } from '@/lib/routing/context-home-route'

function contextIcon(scope: ActiveContext['scope']) {
  if (scope === 'organization') {
    return <Building2 className="h-4 w-4 shrink-0" aria-hidden="true" />
  }
  return <User className="h-4 w-4 shrink-0" aria-hidden="true" />
}

function contextLabel(context: ActiveContext): string {
  if (context.scope === 'organization') {
    const suffix = context.isOwner ? ' (Owner)' : context.isAdmin ? ' (Admin)' : ''
    return `${context.displayName}${suffix}`
  }
  return context.displayName
}

function contextDescription(context: ActiveContext): string {
  if (context.scope === 'organization') {
    return 'Organization workspace'
  }
  return 'Personal workspace'
}

export function ContextSwitcher() {
  const navigate = useNavigate()
  const { accountData } = useAuth()
  const { activeContext, availableContexts, isLoading, switchContext } = useActiveContext()

  const isActualAdmin = accountData?.is_admin === true
  if (!isActualAdmin || isLoading || !activeContext || availableContexts.length <= 1) {
    return null
  }

  const personalContexts = availableContexts.filter((c) => c.scope === 'personal')
  const orgContexts = availableContexts.filter((c) => c.scope === 'organization')

  const handleSelect = (context: ActiveContext) => {
    if (context.id === activeContext.id) return
    switchContext(context)
    navigate(getHomeRouteForContext(context))
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className="gap-2 max-w-[12rem] sm:max-w-[16rem] h-9 px-2.5"
          aria-label={`Workspace: ${contextLabel(activeContext)}. Change workspace.`}
        >
          {contextIcon(activeContext.scope)}
          <span className="truncate text-fluid-xs font-medium">{contextLabel(activeContext)}</span>
          <ChevronDown className="h-4 w-4 opacity-50 shrink-0" aria-hidden="true" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-64">
        <DropdownMenuLabel className="text-xs font-normal text-muted-foreground">
          Switch workspace
        </DropdownMenuLabel>
        <DropdownMenuSeparator />

        {personalContexts.length > 0 && (
          <>
            <DropdownMenuLabel className="text-xs text-muted-foreground">
              Personal
            </DropdownMenuLabel>
            {personalContexts.map((context) => (
              <DropdownMenuItem
                key={context.id}
                onClick={() => handleSelect(context)}
                className={activeContext.id === context.id ? 'bg-accent' : ''}
              >
                <User className="h-4 w-4 mr-2 shrink-0" aria-hidden="true" />
                <div className="min-w-0">
                  <div className="truncate font-medium">{context.displayName}</div>
                  <div className="text-xs text-muted-foreground">{contextDescription(context)}</div>
                </div>
              </DropdownMenuItem>
            ))}
          </>
        )}

        {orgContexts.length > 0 && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuLabel className="text-xs text-muted-foreground">
              Organization
            </DropdownMenuLabel>
            {orgContexts.map((context) => (
              <DropdownMenuItem
                key={context.id}
                onClick={() => handleSelect(context)}
                className={activeContext.id === context.id ? 'bg-accent' : ''}
              >
                <Building2 className="h-4 w-4 mr-2 shrink-0" aria-hidden="true" />
                <div className="min-w-0">
                  <div className="truncate font-medium">{contextLabel(context)}</div>
                  <div className="text-xs text-muted-foreground">{contextDescription(context)}</div>
                </div>
              </DropdownMenuItem>
            ))}
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
